// Форматирование чисел, дат, валют и относительного времени (Intl).
import i18n from '@/i18n';

const cache = new Map<string, Intl.NumberFormat>();
function nf(opts: Intl.NumberFormatOptions = {}): Intl.NumberFormat {
  const key = `${i18n.language}:${JSON.stringify(opts)}`;
  let f = cache.get(key);
  if (!f) {
    f = new Intl.NumberFormat(i18n.language, opts);
    cache.set(key, f);
  }
  return f;
}

export function fmt(n: number): string {
  return nf({ maximumFractionDigits: 0 }).format(Math.floor(n));
}

/** Короткая запись для узкого экрана: 999 → 999, 1000 → 1к, 12 500 → 12,5к, 1 000 000 → 1м, 2,5 млрд → 2,5б. */
export function short(n: number): string {
  const lang = i18n.language;
  const units: [number, string][] =
    lang === 'zh'
      ? [
          [1e8, '亿'],
          [1e4, '万'],
        ]
      : lang === 'ru'
        ? [
            [1e9, 'б'],
            [1e6, 'м'],
            [1e3, 'к'],
          ]
        : [
            [1e9, 'B'],
            [1e6, 'M'],
            [1e3, 'k'],
          ];
  const abs = Math.abs(n);
  for (const [v, u] of units) {
    if (abs >= v) {
      const x = n / v;
      // 1–9,9 — с одним знаком после запятой; от 10 — целые (12к, 250к)
      const digits = Math.abs(x) < 10 ? 1 : 0;
      const value = Math.floor(x * 10 ** digits) / 10 ** digits;
      return `${nf({ maximumFractionDigits: digits }).format(value)}${u}`;
    }
  }
  return fmt(n);
}

/** Крупные числа словами для панели ресурсов: «4,2 тыс.», «11 тыс.», «1,5 млн» (EN — 4.2K). */
export function shortWords(n: number): string {
  if (i18n.language === 'zh') return short(n);
  if (i18n.language !== 'ru') return short(n).toUpperCase();
  const units: [number, string][] = [
    [1e9, ' млрд'],
    [1e6, ' млн'],
    [1e3, ' тыс.'],
  ];
  const abs = Math.abs(n);
  for (const [v, u] of units) {
    if (abs >= v) {
      const x = n / v;
      const digits = Math.abs(x) < 10 ? 1 : 0;
      const value = Math.floor(x * 10 ** digits) / 10 ** digits;
      return `${nf({ maximumFractionDigits: digits }).format(value)}${u}`;
    }
  }
  return fmt(n);
}

/** Локаль дат: ru-RU, en-GB, zh-CN. */
export function dateLocale(): string {
  return i18n.language === 'ru' ? 'ru-RU' : i18n.language === 'zh' ? 'zh-CN' : 'en-GB';
}

export function money(amount: number, currency: string): string {
  if (currency === 'XTR') return `${fmt(amount)} ⭐`;
  // Неизвестный код валюты не должен ронять экран: Intl бросает RangeError
  if (!/^[A-Z]{3}$/.test(currency)) return `${fmt(amount)} ${currency}`;
  return nf({ style: 'currency', currency, maximumFractionDigits: currency === 'RUB' ? 0 : 2 }).format(
    amount,
  );
}

export function dateTime(d: string | Date): string {
  return new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeStyle: 'short' }).format(
    new Date(d),
  );
}

export function date(d: string | Date): string {
  return new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }).format(new Date(d));
}

/** Таймер: 1:05:09 / 4:59 / 0:07. */
export function duration(sec: number): string {
  const s = Math.max(0, Math.ceil(sec));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
  return `${m}:${String(ss).padStart(2, '0')}`;
}

/** Долгий срок: «7 д 3 ч», «12 ч 30 мин», «4:59» (для щитов и наград на дни). */
export function span(sec: number): string {
  const s = Math.max(0, Math.ceil(sec));
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const [D, H, M] =
    i18n.language === 'ru'
      ? ['д', 'ч', 'мин']
      : i18n.language === 'zh'
        ? ['天', '小时', '分']
        : ['d', 'h', 'min'];
  if (d > 0) return h > 0 ? `${d} ${D} ${h} ${H}` : `${d} ${D}`;
  if (h > 0) return m > 0 ? `${h} ${H} ${m} ${M}` : `${h} ${H}`;
  return duration(s);
}

export function relative(d: string | Date): string {
  const diff = (new Date(d).getTime() - Date.now()) / 1000;
  const rtf = new Intl.RelativeTimeFormat(i18n.language, { numeric: 'auto' });
  const abs = Math.abs(diff);
  if (abs < 60) return rtf.format(Math.round(diff), 'second');
  if (abs < 3600) return rtf.format(Math.round(diff / 60), 'minute');
  if (abs < 86400) return rtf.format(Math.round(diff / 3600), 'hour');
  return rtf.format(Math.round(diff / 86400), 'day');
}

/** Плюрализация через Intl.PluralRules (для мест без i18next). */
export function plural(n: number, forms: Partial<Record<Intl.LDMLPluralRule, string>>): string {
  const rule = new Intl.PluralRules(i18n.language).select(n);
  return (forms[rule] ?? forms.other ?? '').replace('{n}', fmt(n));
}

/** «в сети» или «последний раз в сети 5 минут назад» — неприметная строка под именем игрока. */
export function lastSeen(online: boolean | undefined, at: string | null | undefined): string {
  if (online) return i18n.t('game:seen.online');
  if (!at) return '';
  return i18n.t('game:seen.ago', { when: relative(at) });
}
